/* eslint-disable complexity */
import { getCurrLayer, isLayerSeasonAgnostic } from "../utils/layerData";
import { extractSoilSsurgoZones } from '../utils/layerData/mosaic/soilSsurgo';
import { SEASON_AGNOSTIC } from '../constants/seasonIds';
import {
  BASE_LAYERS,
  PLANTING,
  IN_SEASON,
  IMPORTED_SHAPEFILE,
  SOIL_MAP,
  PRECISION_PLANTING_SUB_LAYERS,
} from '../constants/mapLayerIds';
import {
  IMPORTED_SHAPEFILES_FEATURE_FLAG,
  OTHER_IMAGERY_FEATURE_FLAG,
  CROP_WATER_FEATURE_FLAG,
  PRECISION_PLANTING_MAPS_FEATURE_FLAG,
  DMB_TARGET_POPULATION_FEATURE_FLAG,
  PRESCRIPTION_MAPS_WEB_FEATURE_FLAG,
} from '../constants/featureFlags';
import {
  LOCALE_GROUPS,
  POPULATION_WEIGHT_UNITS,
  FVP_APPLICATION_DRY_UNITS,
} from '../constants/units';
import {
  RECEIVE_SELECTOR_VALS,
  RECEIVE_FEATURES,
  RECEIVE_PRODUCT_ASSETS,
  RECEIVE_SOIL_SSURGO_ZONES,
  REQUEST_APP_INITIALIZER,
  SET_ACTIVE_SELECTOR_VALS,
  SET_ACTIVE_SUB_LAYER,
  SOIL_SSURGO_ZONES_FAILURE,
  RECEIVE_IMPORTED_SHAPEFILE_DATA,
  IMPORTED_SHAPEFILE_DATA_FAILURE,
} from '../constants/actionTypes';

import { extractImportedShapefileProperties } from '../utils/layerData/mosaic/importedShapefile';
import { LegendAction } from "../actions/legendActions";

export const initialState = {
  activeMapLayers: [{ id: '', name: '' }],
  disabledMapLayers: [],
  isSplitViewEnabled: false,
  list: {},
};

/**
 * When adding new values into a layer object, sublayers must be checked. If a sublayer is defined,
 * new values should be nested within it. Otherwise, new values can be returned as-is.
 * @param {Object} list
 * @param {String} layer
 * @param {Object} newValues
 * @param {String} season
 * @param {String} [subLayer]
 * @return {Object}
 */
function structureLayerValue(list, layer, newValues, season, subLayer) {
  if (subLayer) {
    const currLayer = getCurrLayer(list, layer, season);
    return {
      subLayers: {
        ...currLayer.subLayers,
        [subLayer]: { ...currLayer.subLayers[subLayer], ...newValues },
      },
    };
  }
  return newValues;
}

/**
 * Return a new state object for this reducer, given a specific layer and new values
 * to add to the layer. The layer can be season-specific or season-agnostic.
 * @param {Object} state - This reducer's current state
 * @param {String} layer - Layer ID
 * @param {Object} newValues - New values to merge into the specified layer's object
 * @param {String} [season] - Season tag
 * @param {String} [subLayer] - Sublayer ID
 * @return {Object}
 */
function updateLayerValues(state, layer, newValues, season, subLayer) {
  const layerValues = structureLayerValue(
    state.list,
    layer,
    newValues,
    season,
    subLayer
  );
  const seasonId = isLayerSeasonAgnostic(layer) ? SEASON_AGNOSTIC : season;

  return {
    ...state,
    list: {
      ...state.list,
      [seasonId]: {
        ...state.list[seasonId],
        [layer]: {
          ...state.list[seasonId][layer],
          ...layerValues,
        },
      },
    },
  };
}

// TODO - Remove SonarQube disablement after BTR-931 is done.
function layersReducer(state = initialState, action) {
  // NOSONAR
  switch (action.type) {
    case REQUEST_APP_INITIALIZER: {
      return {
        ...state,
        activeMapLayers: action.mapLayerIds.length
          ? action.mapLayerIds.map((layer) => ({ id: layer }))
          : initialState.activeMapLayers,
        isSplitViewEnabled: action.mapLayerIds.length > 1,
      };
    }
    case RECEIVE_SELECTOR_VALS: {
      const { activeVals, layers } = action;
      return {
        ...state,
        activeMapLayers: activeVals.activeMapLayers,
        list: layers,
        isSplitViewEnabled: activeVals.activeMapLayers.length > 1,
      };
    }
    case SET_ACTIVE_SELECTOR_VALS: {
      return {
        ...state,
        activeMapLayers: action.activeMapLayers,
        isSplitViewEnabled: action.activeMapLayers.length > 1,
      };
    }
    case LegendAction.ReceiveLegend: {
      const { layer, legend, season, subLayer } = action;
      let localeGroup = {};
      const validLayers = [PLANTING.POPULATION, PLANTING.TARGET_POPULATION];
      if (
        validLayers.includes(layer) &&
        legend[0].units === POPULATION_WEIGHT_UNITS.FVP
      ) {
        localeGroup = {
          localeGroup: LOCALE_GROUPS.POPULATION_WEIGHT,
        };
      } else if (layer === IN_SEASON.APPLICATIONS) {
        localeGroup = {
          localeGroup:
            legend[0].units === FVP_APPLICATION_DRY_UNITS
              ? LOCALE_GROUPS.APPLICATION_DRY
              : LOCALE_GROUPS.APPLICATION_LIQUID,
        };
      }
      const newLegend = { legend, ...localeGroup };
      return updateLayerValues(state, layer, newLegend, season, subLayer);
    }
    case RECEIVE_FEATURES: {
      const disabledMapLayers = action.keys.reduce(
        (_disabledMapLayers, key, index) => {
          if (!action.values[index].value) {
            if (key === IMPORTED_SHAPEFILES_FEATURE_FLAG) {
              return _disabledMapLayers.concat(IN_SEASON.IMPORTED_SHAPEFILE);
            }
            if (key === OTHER_IMAGERY_FEATURE_FLAG) {
              return _disabledMapLayers.concat(IN_SEASON.OTHER_IMAGERY);
            }
            if (key === CROP_WATER_FEATURE_FLAG) {
              return _disabledMapLayers.concat(IN_SEASON.CROP_WATER);
            }
            if (key === PRECISION_PLANTING_MAPS_FEATURE_FLAG) {
              return _disabledMapLayers.concat(
                Object.values(PRECISION_PLANTING_SUB_LAYERS)
              );
            }
            if (key === DMB_TARGET_POPULATION_FEATURE_FLAG) {
              return _disabledMapLayers.concat(PLANTING.TARGET_POPULATION);
            }
            if (key === PRESCRIPTION_MAPS_WEB_FEATURE_FLAG) {
              return _disabledMapLayers.concat(
                PLANTING.PLANTING_SCRIPT,
                IN_SEASON.APPLICATION_SCRIPT,
                IN_SEASON.FERTILITY_SCRIPT
              );
            }
            if (key === PRECISION_PLANTING_MAPS_FEATURE_FLAG) {
              return _disabledMapLayers.concat(
                Object.values(PRECISION_PLANTING_SUB_LAYERS)
              );
            }
          }

          return _disabledMapLayers;
        },
        []
      );

      return {
        ...state,
        disabledMapLayers: [...state.disabledMapLayers, ...disabledMapLayers],
      };
    }
    case RECEIVE_PRODUCT_ASSETS:
      return action.payload.featuresByFields[0].features.includes(SOIL_MAP)
        ? state
        : {
            ...state,
            disabledMapLayers: [
              ...state.disabledMapLayers,
              BASE_LAYERS.SOIL_SSURGO,
            ],
          };
    case SET_ACTIVE_SUB_LAYER: {
      const { activeSubLayer, layer, season, view } = action;
      const currLayer = getCurrLayer(state.list, layer, season);
      const current = currLayer.activeSubLayerIds || currLayer.current;
      const newactiveSubLayerIds = {
        [currLayer.activeSubLayerIds ? 'activeSubLayerIds' : 'current']:
          current.map((subLayer, index) =>
            index === view ? activeSubLayer : subLayer
          ),
      };
      return updateLayerValues(state, layer, newactiveSubLayerIds, season);
    }
    case RECEIVE_SOIL_SSURGO_ZONES: {
      const zones = { zones: extractSoilSsurgoZones(action.zones) };
      return updateLayerValues(state, BASE_LAYERS.SOIL_SSURGO, zones);
    }
    case SOIL_SSURGO_ZONES_FAILURE: {
      const zones = { zones: [] };
      return updateLayerValues(state, BASE_LAYERS.SOIL_SSURGO, zones);
    }
    case RECEIVE_IMPORTED_SHAPEFILE_DATA: {
      const properties = {
        properties: extractImportedShapefileProperties(
          action.shapefile.features
        ),
      };
      return updateLayerValues(state, IMPORTED_SHAPEFILE, properties);
    }
    case IMPORTED_SHAPEFILE_DATA_FAILURE: {
      const properties = { properties: [] };
      return updateLayerValues(state, IMPORTED_SHAPEFILE, properties);
    }
    default: {
      return state;
    }
  }
}

export default layersReducer;
